import {
  FormSelect,
  FormSelectOption,
  ActionGroup,
  Flex,
  Form,
  FormGroup,
  TextInput,
  Grid,
  GridItem,
} from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import type {
  MedicationRoute,
  MedicationDosageUnit,
  PatientMedication,
  PatientMedicationDosage,
  PatientMedicationId,
  PatientMedicationRouteId,
} from '@app/domain/patient-medication-history/patient-medication-history.interface'
import { hasContent } from '@app/lib/string'
import { ValidationStatusId } from '@app/lib/validators'
import { SmallButton } from '@app/utils/styles/small-button'

interface Props {
  onSubmitMedication?: (
    name: string,
    medicationDosageUnitId: string,
    medicationRouteId: string,
  ) => void
  onEditMedication?: (
    patientMedicationId: string,
    medicationDosageUnitId: string,
    medicationRouteId: string,
    name: string,
  ) => void
  onCancel: () => void
  medicationRoutes: MedicationRoute[]
  medicationDosageUnits: MedicationDosageUnit[]
  patientMedication?: PatientMedication
}

export const EditableMedicationForm = function ({
  onSubmitMedication,
  onEditMedication,
  onCancel,
  medicationRoutes,
  medicationDosageUnits,
  patientMedication,
}: Props) {
  const defaultSelectType = 'Please Choose'

  const [medicationId, setMedicationId] = useState<PatientMedicationId>(null)
  const [name, setName] = useState<string>(null)
  const [route, setRoute] = useState<PatientMedicationRouteId>(null)
  const [dosage, setDosage] = useState<PatientMedicationDosage>(null)
  const [isFormDirty, setIsFormDirty] = useState(false)
  const [nameValidation, setNameValidation] = useState<ValidationStatusId>(
    ValidationStatusId.Default,
  )
  const [routeValidation, setRouteValidation] = useState<ValidationStatusId>(
    ValidationStatusId.Default,
  )
  const [dosageValidation, setDosageValidation] = useState<ValidationStatusId>(
    ValidationStatusId.Default,
  )

  useEffect(() => {
    if (patientMedication) {
      setName(patientMedication.name)
      setRoute(patientMedication.medicationRouteId)
      setDosage(patientMedication.medicationDosageUnitId)
      setMedicationId(medicationId)
      setIsFormDirty(true)
    }
  }, [medicationId, medicationRoutes, patientMedication])

  const isFormValid = useMemo(() => {
    if (isFormDirty) {
      setNameValidation(
        hasContent(name)
          ? ValidationStatusId.Default
          : ValidationStatusId.Error,
      )
      setRouteValidation(
        hasContent(route) && route !== defaultSelectType
          ? ValidationStatusId.Default
          : ValidationStatusId.Error,
      )
      setDosageValidation(
        hasContent(dosage) && dosage !== defaultSelectType
          ? ValidationStatusId.Default
          : ValidationStatusId.Error,
      )
    }
    return hasContent(name) && hasContent(route) && hasContent(dosage)
  }, [isFormDirty, name, route, dosage])

  const resetForm = useCallback(() => {
    setIsFormDirty(false)
    setNameValidation(ValidationStatusId.Default)
    setRouteValidation(ValidationStatusId.Default)
    setDosageValidation(ValidationStatusId.Default)
    setDosage(null)
    setName(null)
    setRoute(null)
  }, [])

  const handleCancel = useCallback(() => {
    resetForm()
    onCancel()
  }, [onCancel, resetForm])

  const onSubmit = useCallback(() => {
    if (isFormValid) {
      if (patientMedication) {
        onEditMedication(patientMedication.id, dosage, route, name)
      } else {
        onSubmitMedication(dosage, route, name)
      }
      resetForm()
    }
  }, [
    isFormValid,
    patientMedication,
    resetForm,
    onEditMedication,
    dosage,
    route,
    name,
    onSubmitMedication,
  ])

  return (
    <>
      <Form>
        <Grid hasGutter md={6}>
          <GridItem span={6}>
            <FormGroup
              label="Name"
              isRequired
              fieldId="name"
              validated={nameValidation}
              helperTextInvalid="Medication name is required"
            >
              <TextInput
                value={name}
                isRequired
                type="text"
                id="name"
                name="Name"
                onChange={setName}
                onBlur={() => setIsFormDirty(true)}
              />
            </FormGroup>
          </GridItem>
        </Grid>
        <Grid hasGutter md={6}>
          <FormGroup
            isRequired
            fieldId="Route"
            label="Route of Administration"
            validated={routeValidation}
            helperTextInvalid="Route is required"
          >
            <FormSelect
              id="route"
              isRequired
              value={route}
              onChange={setRoute}
              onBlur={() => setIsFormDirty(true)}
            >
              <FormSelectOption
                key={0}
                value={null}
                label={defaultSelectType}
              />
              {medicationRoutes.map((medicationRoute) => (
                <FormSelectOption
                  key={medicationRoute.id}
                  value={medicationRoute.id}
                  label={medicationRoute.name}
                />
              ))}
            </FormSelect>
          </FormGroup>
          <FormGroup
            isRequired
            fieldId="Dosage"
            label="Dosage Units"
            validated={dosageValidation}
            helperTextInvalid="Dosage is required"
          >
            <FormSelect
              isRequired
              value={dosage}
              onChange={setDosage}
              onBlur={() => setIsFormDirty(true)}
            >
              <FormSelectOption
                key={0}
                value={null}
                label={defaultSelectType}
              />
              {medicationDosageUnits.map((medicationDosageUnit) => (
                <FormSelectOption
                  key={medicationDosageUnit.id}
                  value={medicationDosageUnit.id}
                  label={medicationDosageUnit.name}
                />
              ))}
            </FormSelect>
          </FormGroup>
        </Grid>
        <ActionGroup style={{ marginLeft: 'auto', marginTop: 10 }}>
          <Flex>
            <SmallButton
              style={{ marginRight: 5 }}
              key="cancel"
              variant="secondary"
              onClick={handleCancel}
            >
              Cancel
            </SmallButton>
            <SmallButton key="confirm" variant="primary" onClick={onSubmit}>
              {patientMedication ? 'Save' : 'Add'}
            </SmallButton>
          </Flex>
        </ActionGroup>
      </Form>
    </>
  )
}
